import axios from "axios";
import { Avatar } from "components/avatar";
import { Button, NoStyleA, OutlineButton, SubtleIconButton } from "components/button";
import { Link } from "react-router-dom";
import GroupedButton from "components/button/grouped";
import DataLabel from "components/dataLabel";
import Field from "components/field";
import PaymentDollarInput from "./components/PaymentDollarInput";
import { useModalContext } from "components/modal";
import SafeArea from "components/safe-area";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import clients from "constants/clients";
import domains from "constants/domains";
import quickbooksDesktopEndpoints from "constants/routes/quickbooks/desktop";
import InvoicesList from "containers/invoicesList";
import { format } from "date-fns";
import Switch from "rc-switch";
import * as React from "react";
import { FaMoneyCheck, FaStripe } from "react-icons/fa";
import { RiErrorWarningLine, RiPencilLine } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { TPayment, TDepositRequest } from "types/payment";
import { TInvoice } from "types/invoice";
import { dollarFormatter } from "utils";
import constructUrl from "utils/constructUrl";
import Placeholder from "views/request/component/placeholder";
import { useTranslation } from 'react-i18next';
import { enUS, es } from "date-fns/locale";
import { TbNotesOff } from "react-icons/tb";
import TooltippedElement from "components/TooltippedElement";
import { Checkbox } from "components/checkbox";

export default function Payment() {
  const { t, i18n } = useTranslation();
  const tBase = "views.payment.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;

  const params = useParams<{ id: string }>();
  const modal = useModalContext(),
    snackbar = useSnackbarContext(),
    queryClient = useQueryClient();

  const { id } = params;
  const [isEditingRefNumber, setIsEditingRefNumber] = React.useState(false);
  const [refNumberDraft, setRefNumberDraft] = React.useState<string>("");

  const query = useQuery<TPayment>(
    ["payment", params.id],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v2/company/payments/${params.id}
  `
        )
        .then((res) => res.data)
  );

  console.log(query.data);

  const { data: depositRequest } = useQuery<TDepositRequest>(
    ["depositRequest", query.data?.fk_deposit_request_id],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/deposit-request/${query.data?.fk_deposit_request_id}`
        )
        .then((res) => res.data),
    {
      enabled: !!query.data?.fk_deposit_request_id,
    }
  );

  const transferFund = useMutation(
    async () =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/payments/${params.id}/transfer
  `
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        query.refetch();
        snackbar.showSnackbar(tr("Fund transferred to user!"));
      },
    }
  );

  const updateQBSync = useMutation(
    async (e: boolean) =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/payments/${params.id}/qbsync?status=${e}
  `
        )
        .then((res) => res.data),
    {
      onMutate: async (e) => {
        await queryClient.cancelQueries(["payment", params.id]);

        const prev = queryClient.getQueryData(["payment", params.id]);

        queryClient.setQueryData(["payment", params.id], (old: any) => ({
          ...old,
          qb_id: e ? null : "disabled",
        }));

        return { prev };
      },
      onError: (err, _, context: any) => {
        queryClient.setQueryData(["payment", params.id], context.prev);
      },
    }
  );

  const handleClickAddToSyncLog = useMutation(
    async () => {
      const client = clients.QUICKBOOKS;
      const domain = domains.DESKTOP;
      const endpoint = quickbooksDesktopEndpoints.SYNC_LOG;
      
      await axios.post(
        constructUrl({ client, domain, endpoint }),
        {
          action: "add_or_modify",
          resource_type: "Payment",
          resource_id: id,
        }
      );
  },
  {
    onSuccess: () => {
      queryClient.invalidateQueries();
      snackbar.showSnackbar(tr("Added to sync log!"));
    },
    onError: () => {
      snackbar.showSnackbar(tr("Error adding to sync log!"), "error");
    },
  }
)

  const resyncPaymentMutation = useMutation(
    async () => {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-payment`,
        { paymentId: id }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        snackbar.showSnackbar(tr("Payment synced successfully!"));
      },
      onError: () => {
        snackbar.showSnackbar(tr("Error syncing payment!"), "error");
      },
    }
  );

  const resyncBadDebtPaymentMutation = useMutation(
    async () => {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/sync-bad-debt-payment`,
        { paymentId: id }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        snackbar.showSnackbar(tr("Bad debt payment synced successfully!"));
      },
      onError: () => {
        snackbar.showSnackbar(tr("Error syncing bad debt payment!"), "error");
      },
    }
  );

  const updateRefNumberMutation = useMutation(
    async (newRefNumber: string | null) => {
      const payload = {
        ref_number: newRefNumber !== null && newRefNumber !== "" ? newRefNumber : null,
      };
      return await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/payments/${id}/updateRefNumber`,
          payload
        )
        .then((res) => res.data);
    },
    {
      onSuccess: () => {
        setIsEditingRefNumber(false);
        queryClient.invalidateQueries(["payment", id]);
        snackbar.showSnackbar(tr("Check/Ref # updated"));
        if (query.data?.is_write_off) {
          resyncBadDebtPaymentMutation.mutate();
        } else {
          resyncPaymentMutation.mutate();
        }
      },
      onError: () => {
        snackbar.showSnackbar(tr("Error updating Check/Ref #"), "error");
      },
    }
  );

  if (query.isLoading) return <Spinner />;

  if (!query.data) return <span>{tr("No Data")}</span>;

  let data = query.data;

  const bg = data.is_voided
    ? "bg-red-200"
    : data.is_deposit
    ? "bg-blue-200"
    : data.is_write_off
    ? "bg-purple-200"
    : "bg-green-200";
  const text = data.is_voided
    ? "text-red-900"
    : data.is_deposit
    ? "text-blue-900"
    : data.is_write_off
    ? "text-purple-900"
    : "text-green-900";

  const voided = data.is_voided ? "line-through" : "";

  const populateLogs = data.logs?.map((log) => {
    return (
      <div key={log.id} className="py-2 text-slate-800">
        <p>{log.description}</p>
        <span className="text-xs">
          {format(new Date(log.timestamp), "MMM dd, yyyy hh:mm aa", {
            locale: locale,
          })}
        </span>
      </div>
    );
  });
  return (
    <div>
      <SafeArea>
        <div className="flex flex-col pb-10 space-y-8">
          <div className="flex justify-between py-4 border-b">
            <h1>{tr("Payment")} #{data.id}</h1>
            <div className="space-x-2">
              {!data.is_voided &&
                data.remaining_total > 0 &&
                (data.fk_request_id || data.fk_project_id) && (
                  <Button
                    primary
                    onClick={() => transferFund.mutate()}
                    isLoading={transferFund.isLoading}
                  >
                    {tr("Transfer Fund to Account")}
                  </Button>
                )}
              {!data.is_voided && (
                <OutlineButton
                  onClick={() => {
                    // Build map of invoice id -> cents applied from this payment so modal uses correct max unapply.
                    // We rely on paid_total_from_payment when the API returns it (v2 payment.invoices[].paid_total_from_payment).
                    const invoiceAppliedFromPayment: Record<string, number> = {};
                    const invList = data.invoices || [];
                    const singleInvoice = invList.length === 1;
                    invList.forEach((inv: TInvoice & { paid_total_from_payment?: number }) => {
                      if (inv?.id == null) return;
                      const applied =
                        inv.paid_total_from_payment != null && typeof inv.paid_total_from_payment === "number"
                          ? inv.paid_total_from_payment
                          : singleInvoice
                            ? data.amount
                            : undefined;
                      if (applied != null && typeof applied === "number") {
                        invoiceAppliedFromPayment[String(inv.id)] = applied;
                      }
                    });
                    modal.setModal({
                      component: (
                        <VoidPaymentModal
                          id={data.id.toString()}
                          remaining={data.remaining_total}
                          paymentAmount={data.amount}
                          invoices={data.invoices}
                          invoiceAppliedFromPayment={invoiceAppliedFromPayment}
                        />
                      ),
                      label: tr("Void Reason"),
                    });
                  }}
                  className="!border-red-600 !text-red-600 hover:!bg-red-600 hover:!text-white"
                >
                  {tr("Void and Refund")}
                </OutlineButton>
              )}
            </div>
          </div>
          <div className={`p-8 rounded flex flex-col ${bg} ${text}`}>
            <span className="flex items-center space-x-2 text-xl">
              <FaMoneyCheck />
              {data.fk_deposit_request_id
                ? <span>{tr("Deposit Request")}</span> 
                : data.is_deposit
                ? <span>{tr("Deposit")}</span>
                : data.is_write_off
                ? <span>{tr("Write-Off")}</span>
                : <span>{tr("Payment")}</span>
              }
            </span>
            <span className={`flex items-center space-x-4`}>
              <span className={`text-7xl ${voided}`}>
                {dollarFormatter(data.amount)}
              </span>
              {data.is_voided && <span className="text-5xl">{tr("Voided")}</span>}
            </span>
          </div>
          {data.is_voided && (
            <div className="text-red-900">
              <span>{tr("Void Reason")}:</span>
              <p className="text-xl">{data.voided_reason}</p>
            </div>
          )}
          <div className="divide-y">
            <div className="py-4">
              <h2>{tr("Payment Info")}</h2>
            </div>
            <div className="grid grid-cols-2 gap-2 py-4">
              {depositRequest ? (
                <div className="flex items-center divide-x-2 divide-slate-300">
                  <DataLabel title={tr("Remaining")} className="pr-2">
                    <span className="text-2xl">
                      {data.is_voided ? dollarFormatter(0) : dollarFormatter(data.remaining_total)}
                    </span>
                  </DataLabel>
                  <DataLabel title={tr("Requested")} className="px-2">
                    <span className="text-2xl">
                      {dollarFormatter(depositRequest.amount_requested || 0)}
                    </span>
                  </DataLabel>
                  <div className="flex flex-col items-start justify-between h-[56px] px-2">
                    <div className="text-[14px] text-[#64768b]">{tr("Status")}</div>
                    <div className={`mx-0.5 px-2 text-[0.8rem] rounded font-bold 
                      ${depositRequest.status === "Paid" 
                        ? "bg-[#e0ede5] text-[#288741]" 
                        : depositRequest.status === "Partially Paid" 
                        ? "bg-[#fce7dc] text-[#e55b06]" 
                        : depositRequest.status === "Declined" || depositRequest.status === "Voided"
                        ? "bg-[#f8d7da] text-[#dc3545]"
                        : "bg-[#fff2de] text-[#fea420]"}
                    `}>
                      {tr(depositRequest.status)}
                    </div>
                    <div />
                  </div>
                  {depositRequest.decline_note && (
                    <DataLabel title={tr("Decline Note")} className="px-2">
                      <TooltippedElement
                        message={tr("View Decline Note")}
                        placement="right"
                        element={
                          <button
                            onClick={(e) => {
                              modal.setModal({
                                label: tr("Decline Note"),
                                component: (
                                  <div className="p-4 text-gray-700 max-w-[500px]">
                                    <div className="whitespace-pre-wrap mb-4">
                                      {depositRequest.decline_note}
                                    </div>
                                    <Button
                                      type="secondary"
                                      onClick={() => modal.setModal()}
                                    >
                                      {tr("Close")}
                                    </Button>
                                  </div>
                                )
                              })
                            }}
                            className="p-2 rounded-full bg-slate-100 hover:bg-slate-300 transition duration-200"
                          >
                            <TbNotesOff size={20} className="text-yellow-600" />
                          </button>
                        }
                      />
        
                    </DataLabel>
                  )}
                </div>
              ) : (
                <DataLabel title={tr("Remaining")}>
                  <span className="text-2xl">
                    {data.is_voided ? dollarFormatter(0) : dollarFormatter(data.remaining_total)}
                  </span>
                </DataLabel>
              )}

              <DataLabel title={tr("Customer")}>
                <NoStyleA to={`/customers/${data.billed_to.id}`}>
                  <Avatar showLabel>{data.billed_to.name}</Avatar>
                </NoStyleA>
              </DataLabel>
              <DataLabel title={tr("Memo")}>
                {data.note || <span className="text-slate-400">{tr("None")}</span>}
              </DataLabel>
              <DataLabel title={tr("Payment Processed")}>
                {data.stripe_id ? (
                  <FaStripe className="text-6xl text-blue-600" />
                ) : (
                  `${tr("Manual")} (${data.payment_method ? tr(data.payment_method) : tr("None")})`
                )}
              </DataLabel>
              {data.stripe_id ? (
                <>
                  <DataLabel title={tr("Stripe Payment ID")}>
                    {data.stripe_id}
                  </DataLabel>
                  <DataLabel title={tr("Statement Descriptor")}>
                    {data.statement_descriptor}
                  </DataLabel>
                  <DataLabel title={tr("Network")}>{data.network}</DataLabel>
                  <DataLabel title={tr("Last 4")}>
                    **** **** **** {data.last4}
                  </DataLabel>
                  <DataLabel title={tr("Expiry")}>
                    {data.exp_month} / {data.exp_year}
                  </DataLabel>
                </>
              ) : (
                <>
                  <DataLabel title={tr("Check/Ref #")}>
                  {isEditingRefNumber ? (
                    <div className="space-y-2 w-64">
                      <Field
                        value={refNumberDraft}
                        onChange={(e) => setRefNumberDraft(e.target.value)}
                        placeholder={tr("Enter reference number")}
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="small"
                          primary
                          onClick={() => updateRefNumberMutation.mutate(refNumberDraft)}
                          isLoading={updateRefNumberMutation.isLoading}
                        >
                          {tr("Save")}
                        </Button>
                        <OutlineButton
                          size="small"
                          onClick={() => {
                            setIsEditingRefNumber(false);
                            setRefNumberDraft(data.ref_number != null ? String(data.ref_number) : "");
                          }}
                        >
                          {tr("Cancel")}
                        </OutlineButton>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>
                        {data.ref_number != null ? data.ref_number : (
                          <span className="text-slate-400">{tr("None")}</span>
                        )}
                      </span>
                      <TooltippedElement
                        message={"Edit"}
                        element={
                          <SubtleIconButton
                            size="small"
                            onClick={() => {
                              setIsEditingRefNumber(true);
                              setRefNumberDraft(data.ref_number != null ? String(data.ref_number) : "");
                            }}
                          >
                            <RiPencilLine />
                          </SubtleIconButton>
                        }
                      />
                    </div>
                  )}
                </DataLabel>
                  <DataLabel title={tr("Received By")}>
                    {data.received_by || (
                      <span className="text-slate-400">{tr("None")}</span>
                    )}
                  </DataLabel>
                  <DataLabel title={tr("Received On")}>
                    {data.received_on ? (
                      format(new Date(data.received_on), "MMM dd,yyyy", {
                        locale: locale,
                      })
                    ) : (
                      <span className="text-slate-400">{tr("None")}</span>
                    )}
                  </DataLabel>
                </>
              )}
              {data.fk_request_id || data.fk_project_id ? (
                <DataLabel title={data.fk_request_id ? tr("Request #") : tr("Project #")}>
                  {data.fk_request_id ? (
                    <Link to={`/requests/${data.fk_request_id}`}>
                      {data.fk_request_id}
                    </Link>
                  ) : data.fk_project_id ? (
                    <Link to={`/projects/${data.fk_project_id}`}>
                      {data.fk_project_id}
                    </Link>
                  ) : (
                    <span className="text-slate-400">{tr("None")}</span>
                  )}
                </DataLabel>
              ) : null}
              <DataLabel title="Quickbooks Desktop">
                {data.qb_id && data.qb_id !== "disabled" ? (
                  <span>{data.qb_id}</span>
                ) : (
                  <>
                    <span className="mr-2">{tr("Toggle Sync")}</span>
                    <Switch
                      checked={!data.qb_id}
                      onChange={(e) => updateQBSync.mutate(e)}
                    />
                  </>
                )}
                <div className="mt-2">
                  <OutlineButton 
                    onClick={() => handleClickAddToSyncLog.mutate()}
                    isLoading={handleClickAddToSyncLog.isLoading}
                  >
                    {tr("Add to Sync Log")}
                  </OutlineButton>
                </div>
              </DataLabel>

              {/* <DataLabel title="Quickbooks Online">
                <span>{tr("Sync Status")}: </span>
                <br className="mt-2" />
                <OutlineButton 
                  onClick={() => data.is_write_off ? resyncBadDebtPaymentMutation.mutate() : resyncPaymentMutation.mutate()}
                  isLoading={data.is_write_off ? resyncBadDebtPaymentMutation.isLoading : resyncPaymentMutation.isLoading}
                >
                  {tr("QBO Sync")}
                </OutlineButton>
              </DataLabel> */}


              <DataLabel title="Quickbooks Online">
                <div>
                  <strong>QBO ID:</strong> {data.qbol_id}
                </div>
                <div>
                  <strong>{tr("Sync Token")}:</strong>
                  {data.qbol_sync_token}
                </div>
                <div>
                  <strong>{tr("Message")}:</strong> {data.qbol_message}
                </div>
                <div className="mb-2">
                  <strong>{tr("Synced At")}:</strong>{" "}
                  {data.qbol_synced_at
                    ? new Date(data.qbol_synced_at).toLocaleString()
                    : tr("Not Synced")}
                </div>
                <OutlineButton 
                  onClick={() => data.is_write_off ? resyncBadDebtPaymentMutation.mutate() : resyncPaymentMutation.mutate()}
                  isLoading={data.is_write_off ? resyncBadDebtPaymentMutation.isLoading : resyncPaymentMutation.isLoading}
                >
                  {tr("QBO Sync")}
                </OutlineButton>
              </DataLabel>
            </div>
          </div>
          <div className="divide-y">
            <div className="py-4">
              <h2>{tr("Applied To")}</h2>
            </div>
            {data.invoices ? (
              <InvoicesList data={data.invoices} />
            ) : (
              <Placeholder
                type="invoice"
                header={tr("No Invoice")}
                label={tr("Invoices applied will appear here.")}
              />
            )}
          </div>
        </div>
        <div className="divide-y">
          <div className="py-4">
            <h2>{tr("Logs")}</h2>
          </div>
          <div className="py-4 divide-y">{populateLogs}</div>
        </div>
      </SafeArea>
    </div>
  );
}

// Build map of invoice id -> applied amount (cents) from payment response.
// Prefers v2 invoices[].paid_total_from_payment when present; else invoices[].applied_amount or applications[].
function getAppliedAmountByInvoiceId(payment: Record<string, unknown> | null | undefined): Record<string, number> {
  const map: Record<string, number> = {};
  if (!payment) return map;
  const invs = payment.invoices as Array<{ id: string | number; applied_amount?: number; paid_total_from_payment?: number }> | undefined;
  if (Array.isArray(invs)) {
    invs.forEach((inv) => {
      if (inv?.id == null) return;
      const cents = typeof inv.paid_total_from_payment === "number" ? inv.paid_total_from_payment : inv.applied_amount;
      if (typeof cents === "number") {
        map[String(inv.id)] = cents;
      }
    });
  }
  const apps = payment.applications as Array<{ invoice_id: string | number; applied_amount?: number; amount?: number }> | undefined;
  if (Array.isArray(apps)) {
    apps.forEach((app) => {
      const invoiceId = app?.invoice_id != null ? String(app.invoice_id) : null;
      const cents = app?.applied_amount != null ? app.applied_amount : app?.amount;
      if (invoiceId != null && typeof cents === "number" && map[invoiceId] == null) {
        map[invoiceId] = cents;
      }
    });
  }
  return map;
}

const VoidPaymentModal = ({
  id,
  remaining,
  paymentAmount,
  invoices,
  invoiceAppliedFromPayment = {},
}: {
  id: string;
  remaining: number;
  paymentAmount: number;
  invoices?: TInvoice[] | null;
  /** Map invoice id -> cents applied from this payment (from v2 payment.invoices[].paid_total_from_payment); used for max unapply */
  invoiceAppliedFromPayment?: Record<string, number>;
}) => {
  const { t } = useTranslation();
  const tBase = "views.payment.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const modal = useModalContext(),
    snackbar = useSnackbarContext(),
    queryClient = useQueryClient();

  const [reason, setReason] = React.useState("");
  const [amount, setAmount] = React.useState<number | null>();
  const [option, setOption] = React.useState<"full" | "partial">("full");
  const [selectedInvoices, setSelectedInvoices] = React.useState<{[key: string]: {selected: boolean, amount: number}}>({});

  // Fetch payment (v2) for per-invoice applied amounts (paid_total_from_payment); used as fallback in modal
  const { data: paymentForVoid } = useQuery<Record<string, unknown> | null>(
    ["payment-void", id],
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v2/company/payments/${id}`)
        .then((res) => res.data),
    { enabled: !!id && !!(invoices?.length) }
  );
  const appliedAmountByInvoiceId = React.useMemo(() => getAppliedAmountByInvoiceId(paymentForVoid), [paymentForVoid]);

  // Applied amount per invoice. When there's only one invoice, use full payment amount so max is never wrong (e.g. $205.36 not $94.36).
  // Otherwise use paymentForVoid.invoices[].paid_total_from_payment or appliedAmountByInvoiceId.
  const getAppliedCentsFromPayment = (invoiceId: string): number | undefined => {
    const fromSingle = invoices?.length === 1 ? paymentAmount : undefined;
    if (fromSingle != null) return fromSingle;
    const invs = paymentForVoid?.invoices as Array<{ id: string | number; paid_total_from_payment?: number }> | undefined;
    const inv = Array.isArray(invs) ? invs.find((i) => String(i.id) === String(invoiceId)) : undefined;
    if (inv?.paid_total_from_payment != null && typeof inv.paid_total_from_payment === "number") return inv.paid_total_from_payment;
    return appliedAmountByInvoiceId[invoiceId];
  };

  // Initialize selected invoices when invoices data is available
  React.useEffect(() => {
    if (invoices && invoices.length > 0) {
      const initialSelection: {[key: string]: {selected: boolean, amount: number}} = {};
      invoices.forEach(invoice => {
        initialSelection[invoice.id] = {
          selected: false,
          amount: 0
        };
      });
      setSelectedInvoices(initialSelection);
    }
  }, [invoices]);

  // When Refund Amount changes, sync each selected invoice's amount to the new allowed max
  // (so e.g. going from $94.36 to $205.36 refund allows the field to show $205.36)
  const getAppliedAmountCentsRef = React.useRef<(inv: InvoiceWithApplied) => number | undefined>(() => undefined);
  type InvoiceWithApplied = TInvoice & { applied_amount?: number; paid_total_from_payment?: number };
  // Single source for "amount applied to this invoice". Single-invoice case uses full payment amount so max is correct.
  const getAppliedAmountCents = (invoice: InvoiceWithApplied) => {
    const fromPayment = getAppliedCentsFromPayment(String(invoice.id));
    if (fromPayment != null && typeof fromPayment === "number") return fromPayment;
    const fromMap = invoiceAppliedFromPayment[String(invoice.id)];
    if (fromMap != null && typeof fromMap === "number") return fromMap;
    if (invoice.paid_total_from_payment != null && typeof invoice.paid_total_from_payment === "number") return invoice.paid_total_from_payment;
    if (invoices?.length === 1) return paymentAmount;
    return appliedAmountByInvoiceId[invoice.id] ?? invoice.applied_amount;
  };
  getAppliedAmountCentsRef.current = getAppliedAmountCents;

  const handleInvoiceSelection = (invoiceId: string, selected: boolean) => {
    if (!selected) {
      setSelectedInvoices(prev => ({ ...prev, [invoiceId]: { ...prev[invoiceId], selected, amount: 0 } }));
      return;
    }
    const invoice = invoices?.find(inv => String(inv.id) === String(invoiceId));
    let appliedDollars = invoice != null ? ((getAppliedAmountCentsRef.current(invoice) ?? 0) / 100) : 0;
    const refundDollars = amount ?? 0;
    const paymentMaxDollars = paymentAmount / 100;
    if (invoices?.length === 1) {
      appliedDollars = Math.max(appliedDollars, Math.min(refundDollars, paymentMaxDollars));
    }
    const otherTotal = Object.entries(selectedInvoices).reduce((sum, [id, d]) =>
      String(id) === String(invoiceId) || !d.selected ? sum : sum + d.amount, 0);
    const maxAllowed = Math.min(appliedDollars, Math.max(0, refundDollars - otherTotal));
    setSelectedInvoices(prev => ({
      ...prev,
      [invoiceId]: {
        ...prev[invoiceId],
        selected: true,
        amount: maxAllowed
      }
    }));
  };

  // Compute max and cap inside handler. Use isOnlyInvoiceInList from render so we don't rely on stale closure (fixes 94.36 cap when typing 1111).
  const handleInvoiceAmountChange = (invoiceId: string, value: number, isOnlyInvoiceInList: boolean) => {
    const refundAmountDollars = amount ?? 0;
    const otherTotal = getTotalSelectedAmountExcluding(invoiceId);
    const maxFromRefund = Math.max(0, refundAmountDollars - otherTotal);
    const paymentMaxDollars = paymentAmount / 100;
    let maxAmountDollars: number;
    if (isOnlyInvoiceInList) {
      maxAmountDollars = Math.min(refundAmountDollars, paymentMaxDollars);
    } else {
      const appliedCents = getAppliedCentsFromPayment(invoiceId);
      maxAmountDollars = appliedCents != null && typeof appliedCents === "number"
        ? appliedCents / 100
        : (invoices?.find((inv) => String(inv.id) === String(invoiceId))?.amount_due ?? 0) / 100;
    }
    const maxForThisInvoice = Math.min(maxAmountDollars, maxFromRefund);
    const prevAmount = selectedInvoices[invoiceId]?.amount ?? 0;
    // When field is 0 and user types 1–4, some formatters send 10/20/30/40; treat as that digit so we don't cap to max.
    const effectiveValue =
      prevAmount === 0 && value >= 10 && value <= 40 && value % 10 === 0 && maxForThisInvoice >= value / 10
        ? value / 10
        : value;
    const limitedAmount = Math.min(effectiveValue, maxForThisInvoice);
    const capped = limitedAmount > 0 ? limitedAmount : 0;
    setSelectedInvoices(prev => ({
      ...prev,
      [invoiceId]: {
        ...prev[invoiceId],
        amount: capped
      }
    }));
  };

  const getTotalSelectedAmount = () => {
    return Object.values(selectedInvoices).reduce((total, invoice) => {
      const calculatedTotal = total + (invoice.selected ? invoice.amount : 0);
      return calculatedTotal;
    }, 0);
  };

  const getTotalSelectedAmountExcluding = (excludeInvoiceId: string | number) => {
    return Object.entries(selectedInvoices).reduce((total, [invId, data]) => {
      if (String(invId) === String(excludeInvoiceId) || !data.selected) return total;
      return total + data.amount;
    }, 0);
  };


  const mutation = useMutation(
    async () => {
      const payload: any = {
        reason,
        amount
      };

      // Only include selected invoices for partial refunds
      if (option === "partial") {
        const selectedInvoiceData = Object.entries(selectedInvoices)
          .filter(([_, data]) => data.selected)
          .map(([invoiceId, data]) => ({
            invoice_id: invoiceId,
            amount: data.amount
          }));
        payload.selected_invoices = selectedInvoiceData;
      }

      return await axios
        .post(
          `
            ${process.env.REACT_APP_SERVER_URL}/api/v1/company/payments/${id}/void?option=${option}
          `,
          payload
        )
        .then((res) => res.data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        modal.setModal();
        snackbar.showSnackbar(tr("Payment voided"));
      },
    }
  );

  return (
    <div className="space-y-4 w-[500px]">
      <GroupedButton>
        <OutlineButton
          $selected={option === "full"}
          size="small"
          onClick={() => setOption("full")}
        >
          {tr("Full")}
        </OutlineButton>
        <OutlineButton
          $selected={option === "partial"}
          size="small"
          onClick={() => setOption("partial")}
        >
          {tr("Partial")}
        </OutlineButton>
      </GroupedButton>




      {option === "partial" ? (
        <>
          <div className="space-y-2">
            <div>
              {tr("Initial Remaining")}: {dollarFormatter(remaining)}
            </div>
            <div>
              {tr("Calculated Remaining")}: {dollarFormatter(Math.max(0, remaining - ((amount || 0) * 100)))}
            </div>
            <div>
              {tr("Amount to be Unapplied")}: {
                (() => {
                  const amt = amount ?? 0;
                  const remainingAfterRefund = remaining - amt * 100;
                  if (remainingAfterRefund < 0) {
                    return dollarFormatter(Math.min(paymentAmount - remaining, amt * 100 - remaining))
                  } else {
                    return dollarFormatter(0)
                  }
                })()
              }
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("Refund Amount")}
            </label>
            <PaymentDollarInput
              autoFocus
              value={amount ?? undefined}
              onValueChange={(raw) => {
                const maxRefundDollars = paymentAmount / 100;
                const capped =
                  raw === undefined || raw === null
                    ? undefined
                    : raw > maxRefundDollars
                      ? maxRefundDollars
                      : raw;
                setAmount(capped ?? null);
                setSelectedInvoices((prev) => {
                  const next = { ...prev };
                  Object.keys(next).forEach((invoiceId) => {
                    next[invoiceId] = { ...next[invoiceId], selected: false, amount: 0 };
                  });
                  return next;
                });
              }}
              placeholder={`${tr("Refund Amount")} (max: ${dollarFormatter(paymentAmount)})`}
            />
          </div>
        </>
      ) : (
        <div className="flex items-center p-2 text-yellow-700 bg-yellow-100 border border-yellow-700 rounded">
          <RiErrorWarningLine className="mr-2" /> {tr("Invoices with this payment attached will be marked as unpaid.")}
        </div>
      )}

      {/* Invoice Selection - Only show for partial refunds */}
      {option === "partial" && invoices && invoices.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-medium text-gray-900">{tr("Select Invoices to Unapply")}</h3>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
            {invoices.map((invoice) => {
              const refundFullyAllocated = getTotalSelectedAmount() >= (amount ?? 0);
              const thisInvoiceNotSelected = !selectedInvoices[invoice.id]?.selected;
              const disableCheckbox =
                Math.max(0, remaining - ((amount || 0) * 100)) > 0 ||
                (remaining === (amount || 0) * 100) ||
                (thisInvoiceNotSelected && refundFullyAllocated);
              return (
              <div
                key={invoice.id}
                className={`p-3 border-b border-gray-100 last:border-b-0 ${
                  Math.max(0, remaining - ((amount || 0) * 100)) > 0 || (remaining === (amount || 0) * 100) || (thisInvoiceNotSelected && refundFullyAllocated)
                    ? "bg-gray-300"
                    : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      type="checkbox"
                      checked={selectedInvoices[invoice.id]?.selected || false}
                      disabled={disableCheckbox}
                      onChange={(e) => handleInvoiceSelection(invoice.id, (e.target as HTMLInputElement).checked)}
                    />
                    <div>
                      <div className="font-medium">Invoice #{invoice.id}</div>
                      <div className="text-sm text-gray-500">
                        {tr("Total")}: {dollarFormatter(invoice.actual_total)} | {tr("Amount Due")}: {dollarFormatter(invoice.amount_due)}
                      </div>
                      <div className="text-sm text-gray-600 mt-0.5">
                        {tr("Applied from this payment")}: {getAppliedAmountCents(invoice) != null
                          ? dollarFormatter(getAppliedAmountCents(invoice)!)
                          : "—"}
                      </div>
                    </div>
                  </div>
                  {selectedInvoices[invoice.id]?.selected && (
                    <div className="w-32">
                      <PaymentDollarInput
                        value={selectedInvoices[invoice.id]?.amount}
                        onValueChange={(val) => handleInvoiceAmountChange(invoice.id, val ?? 0, invoices?.length === 1)}
                        placeholder={`(max: ${dollarFormatter(getAppliedAmountCents(invoice) ?? invoice.amount_due)})`}
                        disabled={Math.max(0, remaining - ((amount || 0) * 100)) > 0}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
          {Object.values(selectedInvoices).some(inv => inv.selected) && (
            <div className="space-y-1">
              <div className="text-sm text-gray-600">
                {tr("Total Selected Amount")}: {dollarFormatter(getTotalSelectedAmount() * 100)}
              </div>
              {getTotalSelectedAmount() > (amount ?? 0) && (
                <div className="text-sm text-red-600">
                  {tr("Total selected amount cannot exceed Refund Amount")} ({dollarFormatter((amount ?? 0) * 100)}).
                </div>
              )}
              {((amount || 0) * 100 - remaining) > 0 && getTotalSelectedAmount() * 100 !== ((amount || 0) * 100 - remaining) && (
                <div className="text-sm text-red-600">
                  {tr("Selected amount must equal")} {dollarFormatter((amount || 0) * 100 - remaining)} {tr("to proceed")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      <Field
        type="textarea"
        label={tr("Please state your reason of voiding this payment")}
        minRows={4}
        onChange={(e) => setReason(e.target.value)}
        placeholder={tr("Your reason....")}
        setBackgroundToRed={
          reason !== null && reason !== undefined && reason !== ""
            ? false
            : (
                (remaining >= (amount || 0) * 100 && amount !== undefined && amount !== null) ||
                ((amount || 0) * 100 > remaining && 
                  invoices && 
                  invoices.length > 0 && 
                  Object.values(selectedInvoices).some(inv => inv.selected && inv.amount > 0))
              )
        }
      />
      <div className="flex justify-end space-x-2">
        <Button
          type="submit"
          primary
          disabled={
            !reason ||
            (option === "partial" && (!amount || amount < 0 || (amount * 100) > paymentAmount)) ||
            (option === "partial" && invoices && invoices.length > 0 && Object.values(selectedInvoices).some(inv => inv.selected && inv.amount <= 0)) ||
            (option === "partial" && invoices && invoices.length > 0 && (amount || 0) * 100 > remaining && getTotalSelectedAmount() * 100 !== ((amount || 0) * 100 - remaining))
          }
          isLoading={mutation.isLoading}
          onClick={() => mutation.mutate()}
        >
          {tr("Submit")}
        </Button>
        <OutlineButton onClick={() => modal.setModal()}>{tr("Cancel")}</OutlineButton>
      </div>
    </div>
  );
};

const CancelWriteOffModal = ({
  id,
  amount,
}: {
  id: string;
  amount: number;
}) => {
  const { t } = useTranslation();
  const tBase = "views.payment.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const modal = useModalContext(),
    snackbar = useSnackbarContext(),
    queryClient = useQueryClient();

  const [reason, setReason] = React.useState("");

  const mutation = useMutation(
    async () => {
      // Cancel the write-off in QuickBooks and mark as voided in database
      const cancelResult = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/void-write-off`,
        {
          paymentId: parseInt(id),
          reason,
        }
      );
      
      return { cancelResult: cancelResult.data };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        modal.setModal();
        snackbar.showSnackbar(tr("Write-off cancelled successfully"));
      },
      onError: (error) => {
        console.error("Error cancelling write-off:", error);
        snackbar.showSnackbar("Error cancelling write-off", "error");
      },
    }
  );

  return (
    <div className="space-y-4 w-[500px]">
      <div className="bg-gray-50 px-4 py-2 rounded-lg font-semibold text-gray-700">
        {tr("Amount")}: {dollarFormatter(amount)}
      </div>
      <Field
        type="textarea"
        label={tr("Please state your reason for voiding this write-off")}
        minRows={4}
        onChange={(e) => setReason(e.target.value)}
        placeholder={tr("Your reason....")}
      />
      <div className="flex justify-end space-x-2">
        <Button
          type="submit"
          primary
          disabled={!reason}
          isLoading={mutation.isLoading}
          onClick={() => mutation.mutate()}
        >
          {tr("Submit")}
        </Button>
        <OutlineButton onClick={() => modal.setModal()}>{tr("Cancel")}</OutlineButton>
      </div>
    </div>
  );
};
